import { metaRepository } from './repositories/meta.ts';

/**
 * Asks the browser to keep this database when storage runs low.
 *
 * Hazard H3 in COMPLIANCE.md: iOS Safari evicts IndexedDB for a site that is
 * not installed to the home screen after seven days without a visit. A trainee
 * who logs a run of nights, does not open the app for a fortnight, and has not
 * yet synced would lose them. Persistent storage exempts the site; installing
 * to the home screen exempts it on iOS regardless.
 *
 * Requested once, on the first write rather than on load, because browsers
 * weigh engagement when deciding and a request during genuine use is far more
 * likely to be granted than one on a cold first visit.
 *
 * The outcome is recorded, never acted on here. Phase 13 reads it and offers
 * install instructions if it was refused.
 */

let requested = false;

export async function ensurePersistentStorage(): Promise<void> {
  if (requested) return;
  requested = true;

  // Absent in Node during tests, and in older Safari.
  if (typeof navigator === 'undefined' || (navigator.storage as unknown) === undefined) return;
  if (typeof navigator.storage.persist !== 'function') return;

  try {
    const already = await navigator.storage.persisted();
    const granted = already || (await navigator.storage.persist());
    await metaRepository.setStoragePersisted(granted);
  } catch {
    // A refusal is information, not a failure. Never let this break a write —
    // the entry matters more than knowing whether storage is durable.
    await metaRepository.setStoragePersisted(false);
  }
}

/** Exported for tests, which need each case to start from a clean slate. */
export function resetPersistenceRequestForTests(): void {
  requested = false;
}
